import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useAddClientDomain, useClientDomains, useRemoveClientDomain } from '@/queries/clientMeetings'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { LoadError } from './LoadError'

export function EmailDomainsCard({ companyId }: { companyId: string }) {
  const domainsQuery = useClientDomains(companyId)
  const domains = domainsQuery.data?.data ?? []
  const add = useAddClientDomain(companyId)
  const remove = useRemoveClientDomain(companyId)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function addDomain() {
    setError(null)
    try {
      await add.mutateAsync(draft.trim().toLowerCase())
      setDraft('')
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not add the domain'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email domains (for Fireflies)</CardTitle>
        <CardDescription>Fireflies attendees on these domains who aren't Etainabl users count as this client.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {domainsQuery.isError && !domainsQuery.data ? (
          <LoadError title="Email domains didn't load" onRetry={() => domainsQuery.refetch()} />
        ) : domainsQuery.isLoading ? (
          <p className="text-[13px] italic text-etn-text-muted">Loading…</p>
        ) : domains.length ? (
          <ul className="divide-y divide-etn-border-row text-[13px]">
            {domains.map(item => (
              <li key={item.domain} data-testid={`domain-${item.domain}`} className="flex items-center justify-between gap-3 py-2">
                <span className="flex items-center gap-2">
                  <span className="font-medium text-etn-text">{item.domain}</span>
                  <Badge variant={item.source === 'users' ? 'neutral' : 'info'}>{item.source === 'users' ? 'From users' : 'Added'}</Badge>
                </span>
                <Button size="xs" variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate(item.domain)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[13px] italic text-etn-text-muted">No domains yet</p>
        )}
        {domainsQuery.data && (
          <div className="flex max-w-md gap-2">
            <Input aria-label="Domain to add" placeholder="placefirst.co.uk" value={draft} onChange={event => setDraft(event.target.value)} />
            <Button disabled={!draft.trim() || add.isPending} onClick={addDomain}>
              Add domain
            </Button>
          </div>
        )}
        {error && <p className="text-[12.5px] text-etn-danger-text">{error}</p>}
      </CardContent>
    </Card>
  )
}
